import * as React from "react";
import { FormPrompt, FormPromptWithSaveProps } from "shared/prompt";
import { HTMLChakraProps, chakra, Box, useDisclosure } from "@chakra-ui/react";
import {
  useAuditChartReadOnly,
  useChartTrackingContext,
  useSpeechToTextContext,
} from "contexts";
import { useListChartElements } from "modules/charts-shared/hooks";
import { FieldValues, useFormContext } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
import { SectionSuggestionPopup } from "modules/charts-shared/components/hher-ally/SectionSuggestionPopup";
import {
  hasShownWarning,
  markWarningShown,
} from "modules/charts-shared/components/hher-ally/warningShownTracker";
import { resolveSectionSuggestionCode } from "modules/charts-shared/hooks/sectionSuggestionCode";
import { useSectionSuggestions } from "modules/charts-shared/hooks/useSectionSuggestions";

type SectionSuggestionWarningGateProps = {
  encounterId: string;
  sectionCode: string;
};

function SectionSuggestionWarningGate({
  encounterId,
  sectionCode,
}: SectionSuggestionWarningGateProps) {
  const { data: sectionSuggestions } = useSectionSuggestions(
    encounterId,
    sectionCode
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const warningKey = `${encounterId}:${sectionCode}`;

  React.useEffect(() => {
    if (!sectionSuggestions || sectionSuggestions.length === 0) {
      return;
    }
    if (hasShownWarning(warningKey)) {
      return;
    }
    if (sectionSuggestions.some((suggestion) => !suggestion.isReviewed)) {
      markWarningShown(warningKey);
      onOpen();
    }
  }, [warningKey, sectionSuggestions, onOpen]);

  return <SectionSuggestionPopup isOpen={isOpen} onOk={onClose} />;
}

type ChartFormProps = {
  onRouteChangeSave: FormPromptWithSaveProps["onSave"];
  onSilentSave?: FormPromptWithSaveProps["onSilentSave"];
  isPromptSaveLoading: FormPromptWithSaveProps["isSaveLoading"];
  isDirty: boolean;
  disabled?: boolean;
  sectionId?: string;
  setChartElementsList?(chartElementList: Record<string, string>[]): void;
  autoSaveOnNavigate?: boolean;
} & React.ComponentProps<"form"> &
  HTMLChakraProps<"form">;

function ChartForm(props: ChartFormProps) {
  const {
    onRouteChangeSave,
    onSilentSave,
    isDirty,
    isPromptSaveLoading,
    sectionId,
    children,
    disabled,
    autoSaveOnNavigate = false,
    ...formProps
  } = props;

  const formRef = React.useRef<HTMLFormElement>(null);
  const {
    updateChartElements,
    registerActiveSectionForm,
    unregisterActiveSectionForm,
  } = useChartTrackingContext();
  const { isAuditChartReadOnly } = useAuditChartReadOnly();
  const isInteractionLocked = Boolean(disabled || isAuditChartReadOnly);
  const location = useLocation();
  const fromAudits = location.pathname.includes("audits");
  const fromMedicalRecords = location.pathname.includes("medical-records");
  const { encounterId = "" } = useParams<{ encounterId: string }>();
  const resolvedSectionCode = resolveSectionSuggestionCode(sectionId ?? "");

  const sectionFormContext = useFormContext<FieldValues>();
  const sectionSetValue = sectionFormContext?.setValue;
  const sectionGetValues = sectionFormContext?.getValues;

  React.useEffect(() => {
    if (!sectionId || !encounterId || !sectionSetValue || !sectionGetValues) {
      return;
    }
    registerActiveSectionForm({
      encounterId,
      sectionId,
      setValue: sectionSetValue,
      getValues: sectionGetValues,
      isReadOnly: isInteractionLocked,
      isDirty,
      isSaving: isPromptSaveLoading,
    });
    return () => {
      unregisterActiveSectionForm(sectionId);
    };
  }, [
    encounterId,
    sectionId,
    sectionSetValue,
    sectionGetValues,
    registerActiveSectionForm,
    unregisterActiveSectionForm,
    isInteractionLocked,
    isDirty,
    isPromptSaveLoading,
  ]);

  const secondaryText = React.useMemo(
    () =>
      fromAudits
        ? "Audit changes are pending in this section. Save them before navigating to another section."
        : ["SHPI", "SROS", "SPEX", "SCHC", "SASS"].includes(sectionId || "")
        ? `You must fill out every subsection
        for this section before the chart can be completed.`
        : "",
    [fromAudits, sectionId]
  );

  const { setCodesList } = useSpeechToTextContext();
  useListChartElements(children, updateChartElements, setCodesList);

  return (
    <>
      <FormPrompt
        mainText="You have unsaved changes in this section!"
        onSave={onRouteChangeSave}
        title="Warning"
        when={isDirty && !isInteractionLocked}
        isSaveLoading={isPromptSaveLoading}
        secondaryText={secondaryText}
        skipDialog={autoSaveOnNavigate}
        onSilentSave={autoSaveOnNavigate ? onSilentSave : undefined}
      />
      <Box position="relative" height="100%">
        <chakra.fieldset
          minW={fromMedicalRecords ? "100vw" : "unset"}
          disabled={isInteractionLocked}
          pointerEvents={isInteractionLocked ? "none" : undefined}
        >
          <chakra.form
            ref={formRef}
            padding="1.5rem"
            sx={{ "> :not(:last-child)": { marginBottom: "1rem" } }}
            height="100%"
            disabled={disabled}
            {...formProps}
          >
            {children}
          </chakra.form>
        </chakra.fieldset>
      </Box>
      {resolvedSectionCode && !fromAudits && !fromMedicalRecords ? (
        <SectionSuggestionWarningGate
          encounterId={encounterId}
          sectionCode={resolvedSectionCode}
        />
      ) : null}
    </>
  );
}

export { ChartForm };
